import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/shared/forms';
import { Button, Dialog, MarkdownEditor } from '@/shared/ui';

import styles from './Kanban.module.css';

const MAX_LENGTH = 5000;

interface Props {
  columnName: string;
  initialValue: string;
  onSave: (description: string) => Promise<unknown>;
  onClose: () => void;
}

/** Small Markdown editor for a column description. An empty text removes it. */
export function ColumnDescriptionDialog({ columnName, initialValue, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  return (
    <Dialog title={t('columns.descriptionTitle', { name: columnName })} onClose={onClose}>
      <form
        className={styles.descriptionForm}
        onSubmit={(event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          onSave(value).then(onClose, (reason: unknown) => {
            setError(reason);
            setPending(false);
          });
        }}
      >
        <MarkdownEditor
          label={t('columns.description')}
          value={value}
          onChange={setValue}
          maxLength={MAX_LENGTH}
          rows={7}
          autoFocus
        />
        <p className={styles.descriptionHint}>{t('content.markdownHint')}</p>
        <FormError error={error} />
        <div className={styles.descriptionActions}>
          <Button type="submit" variant="primary" disabled={pending}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
